import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, CircularProgress, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment-jalaali';
import DOMPurify from 'dompurify';

// Icons
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import InventoryIcon from '@mui/icons-material/Inventory2';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CategoryIcon from '@mui/icons-material/Category';
import HistoryIcon from '@mui/icons-material/History';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NoPhotographyIcon from '@mui/icons-material/NoPhotography';

import productManagementService, { AdminProductItem, ProductStatus } from '../services/productManagement.service';
import storeAdminService from '../services/storeAdmin.service';
import { ConfirmDialog } from '../components/common';

import './EntityManagement.css';
import './ProductDetailsModal.css';

// Format date helper
const formatDate = (date: string | Date | undefined): string => {
  if (!date) return '-';
  return moment(date).locale('fa').format('jYYYY/jMM/jDD HH:mm');
};

// Helper to get product image
const getProductImage = (product: any): string | null => {
  if (!product) return null;
  if (product.images && Array.isArray(product.images) && product.images.length > 0) return product.images[0];
  if (product.image) return product.image;
  if (product.thumbnail) return product.thumbnail;
  if (product.coverImage) return product.coverImage;
  return null;
};

// Helper to get price range from capacity fields
const getPriceRange = (product: any): { min: number | null; max: number | null; hasRange: boolean } => {
  const basePrice = product?.price || null;
  const fields = product?.fields || {};
  const accountTypeFields = product?.accountTypeId?.fields || [];

  const capacityField = accountTypeFields.find((f: any) => f?.type === 'capacity_type');
  if (!capacityField) return { min: basePrice, max: basePrice, hasRange: false };

  const capacityValue = fields[capacityField.name];
  if (!capacityValue || typeof capacityValue !== 'object') return { min: basePrice, max: basePrice, hasRange: false };

  const prices: number[] = [];
  Object.values(capacityValue).forEach((val: any) => {
    if (val && typeof val === 'object' && 'price' in val) {
      prices.push(Number(val.price) || 0);
    } else if (typeof val === 'number') {
      prices.push(val);
    }
  });

  if (prices.length === 0) return { min: basePrice, max: basePrice, hasRange: false };

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { min, max, hasRange: min !== max };
};

// Format price display
const formatPrice = (product: any): string => {
  const { min, max, hasRange } = getPriceRange(product);
  if (min === null && max === null) return '-';
  if (!hasRange || min === max) {
    return (min || max || 0).toLocaleString('fa-IR') + ' تومان';
  }
  return `${min!.toLocaleString('fa-IR')} - ${max!.toLocaleString('fa-IR')} تومان`;
};

const statusLabels: Record<string, string> = {
  'active': 'منتشر شده',
  'inactive': 'غیرفعال',
  'pending_review': 'در انتظار',
  'rejected': 'رد شده',
  'out_of_stock': 'ناموجود'
};

const getStatusTagClass = (status: string): string => {
  if (status === 'active') return 'tag tag-sm tag-success';
  if (status === 'inactive') return 'tag tag-sm tag-inactive';
  if (status === 'rejected') return 'tag tag-sm tag-danger';
  if (status === 'pending_review') return 'tag tag-sm tag-warning';
  if (status === 'out_of_stock') return 'tag tag-sm tag-warning';
  return 'tag tag-sm tag-inactive';
};

// Get display value for dynamic object values
const getDisplayValue = (value: any): string => {
  if (value === undefined || value === null || value === '') return '';
  if (Array.isArray(value)) {
    return value.map(v => getDisplayValue(v)).filter(v => v).join('، ');
  }
  if (typeof value === 'object') {
    if (value.name) return value.name;
    if (value.label) return value.label;
    if (value.title) return value.title;
    if (value.displayName) return value.displayName;
    if (value._id && Object.keys(value).length <= 2) return String(value._id);
    const keys = Object.keys(value);
    if (keys.length === 1) return getDisplayValue(value[keys[0]]);
    if (keys.length <= 3) {
      return keys.map(k => `${k}: ${getDisplayValue(value[k])}`).join(' | ');
    }
    return '';
  }
  return String(value);
};

// ✅ Walk بازگشتی روی subFields تا فیلدهای nested (فرم تو در تو) هم استخراج شوند
const flattenAccountTypeFields = (list: any[] | undefined): any[] => {
  if (!Array.isArray(list)) return [];
  const out: any[] = [];
  for (const f of list) {
    if (!f) continue;
    out.push(f);
    if (Array.isArray(f.subFields) && f.subFields.length) {
      out.push(...flattenAccountTypeFields(f.subFields));
    }
  }
  return out;
};

// Extract dynamic field values for display
const extractDynamicFields = (product: any): Array<{ label: string; value: string; type: string }> => {
  const result: Array<{ label: string; value: string; type: string }> = [];
  if (!product?.fields || !product?.accountTypeId?.fields) return result;

  const accountTypeFields = flattenAccountTypeFields(product.accountTypeId.fields);
  const productFields = product.fields || {};

  accountTypeFields.forEach((fieldDef: any) => {
    if (fieldDef.type === 'capacity_type' || fieldDef.type === 'credentials' || fieldDef.clientOnly) return;

    const value = productFields[fieldDef.name];
    const isBool =
      (fieldDef.type === 'checkbox' || fieldDef.type === 'radio') &&
      (!fieldDef.options?.items || fieldDef.options.items.length === 0);

    // فیلدهای بولین حتی اگر undefined/null باشند، نمایش "ندارد" داده می‌شوند
    if (!isBool && (value === undefined || value === null || value === '')) return;

    let displayValue = '';

    if (isBool) {
      displayValue = (value === true || value === 'true') ? 'دارد' : 'ندارد';
    } else if (fieldDef.type === 'color') {
      // hex code as string — UI رنگ swatch فقط در ProductFieldsView نمایش داده می‌شود
      displayValue = typeof value === 'string' ? value.toUpperCase() : '';
    } else if (fieldDef.type === 'dropdown' || fieldDef.type === 'radio' || fieldDef.type === 'checkbox') {
      if (fieldDef.options?.dynamicSource) {
        displayValue = getDisplayValue(value);
      } else if (fieldDef.options?.items) {
        if (Array.isArray(value)) {
          displayValue = value.map((v: any) => {
            const option = fieldDef.options.items.find((opt: any) => opt.value === v || opt.value === String(v));
            return option?.label || getDisplayValue(v);
          }).filter((v: string) => v).join('، ');
        } else {
          const option = fieldDef.options.items.find((opt: any) => opt.value === value || opt.value === String(value));
          displayValue = option?.label || getDisplayValue(value);
        }
      } else {
        displayValue = getDisplayValue(value);
      }
    } else {
      displayValue = getDisplayValue(value);
    }

    if (displayValue) {
      result.push({
        label: fieldDef.label || fieldDef.name,
        value: displayValue,
        type: fieldDef.type
      });
    }
  });

  return result;
};

// Get capacity prices
const getCapacityPrices = (product: any): Array<{ name: string; price: number }> => {
  if (!product) return [];
  const fields = product.fields || {};
  const accountTypeFields = product.accountTypeId?.fields || [];
  const capacityField = accountTypeFields.find((f: any) => f?.type === 'capacity_type');
  if (!capacityField) return [];

  const capacityValue = fields[capacityField.name];
  if (!capacityValue || typeof capacityValue !== 'object') return [];

  return Object.entries(capacityValue).map(([key, val]: [string, any]) => {
    const isNew = val && typeof val === 'object' && 'price' in val;
    return {
      name: isNew ? (val.name || key) : key,
      price: isNew ? val.price : val
    };
  }).sort((a, b) => a.price - b.price);
};

const ProductManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [items, setItems] = useState<AdminProductItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);

  // Stats
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, outOfStock: 0 });

  // Filters
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [status, setStatus] = useState<ProductStatus | ''>('');
  const [isVerified, setIsVerified] = useState<string>('');
  const [storeOptions, setStoreOptions] = useState<Array<{ _id: string; name: string }>>([]);
  const [storeId, setStoreId] = useState<string>('');
  const [loadingStores, setLoadingStores] = useState(false);

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState<boolean>(false);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<any | null>(null);
  const [galleryIdx, setGalleryIdx] = useState<number>(0);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  // Action dialogs
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState<AdminProductItem | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load stores list
  useEffect(() => {
    const loadStores = async () => {
      setLoadingStores(true);
      try {
        const res = await storeAdminService.searchStores('', 1, 100);
        setStoreOptions(res.items || []);
      } catch {
        setStoreOptions([]);
      } finally {
        setLoadingStores(false);
      }
    };
    loadStores();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productManagementService.getProducts({
        page,
        limit,
        search: search.trim() || undefined,
        status: (status || undefined) as any,
        isVerified: isVerified ? isVerified === 'true' : undefined,
        storeId: storeId || undefined
      });
      setItems(res.items);
      setTotal(res.meta.totalItems);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت لیست محصولات', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, status, isVerified, storeId, enqueueSnackbar]);

  const fetchStats = useCallback(async () => {
    try {
      const [all, active, inactive, outOfStock] = await Promise.all([
        productManagementService.getProducts({ page: 1, limit: 1 }),
        productManagementService.getProducts({ page: 1, limit: 1, status: 'active' as any }),
        productManagementService.getProducts({ page: 1, limit: 1, status: 'inactive' as any }),
        productManagementService.getProducts({ page: 1, limit: 1, status: 'out_of_stock' as any })
      ]);
      setStats({
        total: all.meta?.totalItems || 0,
        active: active.meta?.totalItems || 0,
        inactive: inactive.meta?.totalItems || 0,
        outOfStock: outOfStock.meta?.totalItems || 0
      });
    } catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const openDetail = async (row: AdminProductItem) => {
    try {
      setDetailLoading(true);
      setDetail(null);
      setGalleryIdx(0);
      setDetailOpen(true);
      const data = await productManagementService.getProductById(row._id);
      setDetail(data);
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در دریافت جزئیات محصول', { variant: 'error' });
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setDetail(null);
    setLightboxSrc(null);
  };

  const openPublish = (row: AdminProductItem) => { setCurrentRow(row); setPublishOpen(true); };
  const openUnpublish = (row: AdminProductItem) => { setCurrentRow(row); setUnpublishOpen(true); };
  const closeActions = () => { setPublishOpen(false); setUnpublishOpen(false); setCurrentRow(null); };

  const handlePublish = async () => {
    if (!currentRow) return;
    try {
      setActionLoading(true);
      await productManagementService.updateStatus(currentRow._id, 'active');
      enqueueSnackbar('محصول منتشر شد', { variant: 'success' });
      closeActions();
      await load();
      await fetchStats();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در انتشار محصول', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!currentRow) return;
    try {
      setActionLoading(true);
      await productManagementService.updateStatus(currentRow._id, 'inactive');
      enqueueSnackbar('محصول متوقف شد', { variant: 'success' });
      closeActions();
      await load();
      await fetchStats();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'خطا در توقف محصول', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSearchInput('');
    setStatus('');
    setIsVerified('');
    setStoreId('');
    setPage(1);
  };

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);
  const hasActiveFilters = search || status || isVerified || storeId;
  const selectedStoreName = storeOptions.find(s => s._id === storeId)?.name || '';

  return (
    <div className="entity-page">
      {/* Header */}
      <div className="entity-header">
        <div>
          <h1 className="entity-title">مدیریت محصولات</h1>
          <p className="entity-subtitle">مدیریت محصولات منتشر شده</p>
        </div>
        <div className="header-actions">
          <button className="header-btn refresh" onClick={() => { load(); fetchStats(); }} title="بروزرسانی">
            <RefreshIcon fontSize="small" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="entity-stats">
        <div className="entity-stat-card">
          <div className="entity-stat-icon blue"><InventoryIcon /></div>
          <div>
            <p className="entity-stat-label">کل محصولات</p>
            <p className="entity-stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon green"><CheckCircleIcon /></div>
          <div>
            <p className="entity-stat-label">منتشر شده</p>
            <p className="entity-stat-value">{stats.active}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon orange"><BlockIcon /></div>
          <div>
            <p className="entity-stat-label">غیرفعال</p>
            <p className="entity-stat-value">{stats.inactive}</p>
          </div>
        </div>
        <div className="entity-stat-card">
          <div className="entity-stat-icon red"><WarningAmberIcon /></div>
          <div>
            <p className="entity-stat-label">ناموجود</p>
            <p className="entity-stat-value">{stats.outOfStock}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="entity-filters">
        <div className="filters-grid">
          <div className="filter-group">
            <label className="filter-label">
              <SearchIcon fontSize="small" />
              جستجو
            </label>
            <div className="filter-search-wrap">
              <input
                className="filter-field"
                type="text"
                placeholder="جستجو..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyPress={handleKeyPress}
              />
              <span className="filter-search-icon">
                <SearchIcon fontSize="small" />
              </span>
            </div>
          </div>

          <div className="filter-group">
            <label className="filter-label">وضعیت</label>
            <select
              className="filter-field"
              value={status}
              onChange={(e) => { setStatus(e.target.value as any); setPage(1); }}
            >
              <option value="">همه وضعیت‌ها</option>
              <option value="active">منتشر شده</option>
              <option value="inactive">غیرفعال</option>
              <option value="pending_review">در انتظار</option>
              <option value="rejected">رد شده</option>
              <option value="out_of_stock">ناموجود</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">تأیید</label>
            <select
              className="filter-field"
              value={isVerified}
              onChange={(e) => { setIsVerified(e.target.value); setPage(1); }}
            >
              <option value="">همه تأیید</option>
              <option value="true">تأیید شده</option>
              <option value="false">تأیید نشده</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">فروشگاه</label>
            <select
              className="filter-field"
              value={storeId}
              onChange={(e) => { setStoreId(e.target.value); setPage(1); }}
            >
              <option value="">{loadingStores ? 'در حال بارگذاری...' : 'همه فروشگاه‌ها'}</option>
              {storeOptions.map((store) => (
                <option key={store._id} value={store._id}>{store.name}</option>
              ))}
            </select>
          </div>

          <div className="filter-group filter-group-actions">
            <button className="filter-btn" onClick={handleSearch}>
              <SearchIcon fontSize="small" />
              اعمال
            </button>
            {hasActiveFilters && (
              <button className="filter-btn outline" onClick={clearFilters}>
                پاک
              </button>
            )}
          </div>
        </div>

        {hasActiveFilters && (
          <div className="filters-active">
            <span className="filters-label">فیلترها:</span>
            {search && (
              <span className="filter-tag">
                {search}
                <button onClick={() => { setSearch(''); setSearchInput(''); }}>×</button>
              </span>
            )}
            {status && (
              <span className="filter-tag">
                {statusLabels[status] || status}
                <button onClick={() => setStatus('')}>×</button>
              </span>
            )}
            {isVerified && (
              <span className="filter-tag">
                {isVerified === 'true' ? 'تأیید شده' : 'تأیید نشده'}
                <button onClick={() => setIsVerified('')}>×</button>
              </span>
            )}
            {storeId && (
              <span className="filter-tag">
                {selectedStoreName}
                <button onClick={() => setStoreId('')}>×</button>
              </span>
            )}
            <button className="filters-clear" onClick={clearFilters}>پاک کردن</button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="entity-table-wrapper">
        {loading ? (
          <div className="entity-loading">
            <CircularProgress size={32} />
          </div>
        ) : items.length === 0 ? (
          <div className="entity-empty">
            <div className="entity-empty-icon"><InventoryIcon style={{ fontSize: 48 }} /></div>
            <p className="entity-empty-text">محصولی یافت نشد</p>
          </div>
        ) : (
          <>
            <table className="entity-table">
              <thead>
                <tr>
                  <th>محصول</th>
                  <th>فروشگاه</th>
                  <th>قیمت</th>
                  <th>موجودی</th>
                  <th>تأیید</th>
                  <th>وضعیت</th>
                  <th style={{ width: 140 }}>عملیات</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const store: any = row.storeId as any;
                  const productImage = getProductImage(row);
                  return (
                    <tr key={row._id}>
                      <td>
                        <div className="entity-info-cell">
                          <div className="entity-image">
                            {productImage ? (
                              <img src={productImage} alt={row.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                            ) : (
                              <InventoryIcon />
                            )}
                          </div>
                          <div className="entity-info">
                            <span className="entity-name">{row.name}</span>
                            {row.sku && <span className="entity-desc">کد: {row.sku}</span>}
                          </div>
                        </div>
                      </td>
                      <td><span className="entity-desc">{store?.name || '-'}</span></td>
                      <td><span className="entity-desc" style={{ whiteSpace: 'nowrap' }}>{formatPrice(row)}</span></td>
                      <td><span className="entity-desc">{row.stock ?? '-'}</span></td>
                      <td>
                        {row.isVerified ? (
                          <span className="tag tag-sm tag-success">تأیید</span>
                        ) : (
                          <span className="tag tag-sm tag-warning">انتظار</span>
                        )}
                      </td>
                      <td>
                        <span className={getStatusTagClass(row.status)}>
                          {statusLabels[row.status] || row.status}
                        </span>
                      </td>
                      <td>
                        <div className="actions-cell">
                          <Tooltip title="جزئیات" arrow>
                            <button className="action-btn edit" onClick={() => openDetail(row)}>
                              <InfoOutlinedIcon />
                            </button>
                          </Tooltip>
                          <Tooltip title="انتشار" arrow>
                            <button
                              className="action-btn toggle active"
                              onClick={() => openPublish(row)}
                              disabled={row.status === 'active'}
                              style={{ opacity: row.status === 'active' ? 0.4 : 1 }}
                            >
                              <PublishedWithChangesIcon />
                            </button>
                          </Tooltip>
                          <Tooltip title="توقف" arrow>
                            <button
                              className="action-btn delete"
                              onClick={() => openUnpublish(row)}
                              disabled={row.status === 'inactive'}
                              style={{ opacity: row.status === 'inactive' ? 0.4 : 1 }}
                            >
                              <PauseCircleOutlineIcon />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Cards */}
            <div className="entity-cards">
              {items.map((row) => {
                const store: any = row.storeId as any;
                const productImage = getProductImage(row);
                return (
                  <div key={row._id} className={`entity-card ${row.status === 'inactive' ? 'inactive' : ''}`}>
                    <div className="entity-card-header">
                      <div className="entity-card-image">
                        {productImage ? (
                          <img src={productImage} alt={row.name} onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                        ) : (
                          <InventoryIcon />
                        )}
                      </div>
                      <div className="entity-card-info">
                        <div className="entity-card-name">{row.name}</div>
                        <div className="entity-card-desc">{store?.name || '-'} - {formatPrice(row)}</div>
                      </div>
                      <div className="entity-card-status">
                        <span className={getStatusTagClass(row.status)}>
                          {statusLabels[row.status] || row.status}
                        </span>
                      </div>
                    </div>
                    <div className="entity-card-actions">
                      <button className="entity-card-btn" onClick={() => openDetail(row)}>
                        <InfoOutlinedIcon fontSize="small" /> جزئیات
                      </button>
                      <button
                        className="entity-card-btn primary"
                        onClick={() => openPublish(row)}
                        disabled={row.status === 'active'}
                      >
                        <PublishedWithChangesIcon fontSize="small" /> انتشار
                      </button>
                      <button
                        className="entity-card-btn danger"
                        onClick={() => openUnpublish(row)}
                        disabled={row.status === 'inactive'}
                      >
                        <PauseCircleOutlineIcon fontSize="small" /> توقف
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="entity-pagination">
              <div className="pagination-info">
                نمایش {startItem} تا {endItem} از {total}
              </div>
              <div className="pagination-controls">
                <button className="pagination-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                  <ChevronRightIcon fontSize="small" />
                </button>
                <span style={{ fontSize: 12 }}>{page} / {totalPages}</span>
                <button className="pagination-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  <ChevronLeftIcon fontSize="small" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Dialog — Redesigned */}
      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth className="product-details-dialog pa-details-dialog">
        <div className="pa-modal-titlebar">
          <span><InventoryIcon fontSize="small" /> جزئیات محصول</span>
          <button className="pa-close" onClick={closeDetail} aria-label="بستن">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <DialogContent className="pa-details-content">
          {detailLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
              <CircularProgress size={32} />
            </div>
          ) : detail ? (() => {
            const d = detail;
            const images: string[] = Array.isArray(d.images) && d.images.length > 0
              ? d.images
              : (getProductImage(d) ? [getProductImage(d) as string] : []);
            const hasImages = images.length > 0;
            const featured = hasImages ? images[Math.min(galleryIdx, images.length - 1)] : null;
            const range = getPriceRange(d);
            const hasDiscount = !range.hasRange && d.discountPrice && d.discountPrice < d.price;
            const off = hasDiscount ? Math.round((1 - Number(d.discountPrice) / Number(d.price)) * 100) : 0;
            const caps = getCapacityPrices(d);
            const dyn = extractDynamicFields(d);
            const vi = d.verificationInfo;
            return (
              <>
                {/* Hero */}
                <div className="pa-hero">
                  <div
                    className={`pa-hero-media ${hasImages ? 'clickable' : ''}`}
                    onClick={() => featured && setLightboxSrc(featured)}
                  >
                    {featured ? (
                      <>
                        <img src={featured} alt={d.name} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        <span className="pa-hero-zoom"><SearchIcon fontSize="small" /></span>
                      </>
                    ) : (
                      <div className="pa-hero-noimg"><NoPhotographyIcon /><span>بدون تصویر</span></div>
                    )}
                  </div>

                  <div className="pa-hero-info">
                    <div className="pa-hero-badges">
                      <span className={getStatusTagClass(d.status)}>{statusLabels[d.status] || d.status}</span>
                      {d.isVerified
                        ? <span className="tag tag-sm tag-success">تأیید شده</span>
                        : <span className="tag tag-sm tag-warning">تأیید نشده</span>}
                      {d.isPostal && (
                        <span className="tag tag-sm tag-warning"><LocalShippingIcon style={{ fontSize: 13, marginLeft: 3, verticalAlign: 'middle' }} /> پستی</span>
                      )}
                    </div>

                    <h2 className="pa-hero-name">{d.name || 'بدون نام'}</h2>

                    {d.storeId?.name && (
                      <div className="pa-hero-store">
                        <span className="pa-store-logo placeholder"><StorefrontIcon fontSize="small" /></span>
                        <span className="pa-store-name">{d.storeId.name}</span>
                      </div>
                    )}

                    <div className="pa-hero-price">
                      {hasDiscount ? (
                        <>
                          <span className="pa-price-final">{Number(d.discountPrice).toLocaleString('fa-IR')} تومان</span>
                          <span className="pa-price-old">{Number(d.price).toLocaleString('fa-IR')} تومان</span>
                          <span className="pa-price-off">{off}٪ تخفیف</span>
                        </>
                      ) : (
                        <span className="pa-price-final">{formatPrice(d)}</span>
                      )}
                    </div>

                    <div className="pa-hero-chips">
                      {d.accountTypeId?.name && (
                        <span className="pa-chip"><CategoryIcon fontSize="inherit" /> {d.accountTypeId.name}</span>
                      )}
                      {d.sku && <span className="pa-chip">SKU: {d.sku}</span>}
                      {/* 🔒 قیمت خرید فروشنده (محرمانه؛ فقط ادمین و خود فروشنده می‌بینند) */}
                      {typeof d.costPrice === 'number' && (() => {
                        const sale = Number(d.discountPrice) > 0 ? Number(d.discountPrice) : Number(d.price || 0);
                        const profit = sale > 0 ? sale - d.costPrice : null;
                        return (
                          <span className="pa-chip" title="قیمت خرید اعلام‌شده توسط فروشنده — به خریدار نمایش داده نمی‌شود">
                            قیمت خرید: {Number(d.costPrice).toLocaleString('fa-IR')} تومان
                            {profit !== null && ` · سود: ${profit < 0 ? '−' : ''}${Math.abs(profit).toLocaleString('fa-IR')}`}
                          </span>
                        );
                      })()}
                      <span className="pa-chip">ایجاد:{formatDate(d.createdAt)}</span>
                      <span className="pa-chip">بروزرسانی: {formatDate(d.updatedAt)}</span>
                      {d.slug && (
                        <a className="pa-chip link" href={`/products/${d.slug}`} target="_blank" rel="noreferrer">
                          مشاهده در سایت
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stat tiles */}
                <div className="pa-stats">
                  <div className="pa-stat">
                    <span className="pa-stat-icon stock"><InventoryIcon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value">{Number(d.stock ?? 0).toLocaleString('fa-IR')}</div>
                      <div className="pa-stat-label">موجودی</div>
                    </div>
                  </div>
                  <div className="pa-stat">
                    <span className="pa-stat-icon sales"><CheckCircleIcon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value">{Number(d.sales ?? 0).toLocaleString('fa-IR')}</div>
                      <div className="pa-stat-label">فروش</div>
                    </div>
                  </div>
                  <div className="pa-stat">
                    <span className="pa-stat-icon views"><SearchIcon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value">{Number(d.views ?? 0).toLocaleString('fa-IR')}</div>
                      <div className="pa-stat-label">بازدید</div>
                    </div>
                  </div>
                  <div className="pa-stat">
                    <span className="pa-stat-icon id"><StorefrontIcon fontSize="small" /></span>
                    <div>
                      <div className="pa-stat-value" style={{ fontSize: '.9rem' }}>{d.storeId?.name || '-'}</div>
                      <div className="pa-stat-label">فروشگاه</div>
                    </div>
                  </div>
                </div>

                {/* Gallery */}
                {hasImages && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">تصاویر محصول <span className="pa-count">{images.length}</span></h3>
                    <div className="pa-gallery">
                      <div className="pa-gallery-main" onClick={() => featured && setLightboxSrc(featured)}>
                        <img src={featured as string} alt={d.name} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                        <span className="pa-hero-zoom"><SearchIcon fontSize="small" /></span>
                      </div>
                      {images.length > 1 && (
                        <div className="pa-gallery-thumbs">
                          {images.map((img, idx) => (
                            <button key={idx} className={`pa-thumb ${idx === galleryIdx ? 'active' : ''}`} onClick={() => setGalleryIdx(idx)}>
                              <img src={img} alt={`تصویر ${idx + 1}`} />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Capacity prices */}
                {caps.length > 0 && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">قیمت حجم‌ها / سایزها <span className="pa-count">{caps.length}</span></h3>
                    <div className="pa-caps">
                      {caps.map((cap, idx) => (
                        <div key={idx} className="pa-cap">
                          <div className="pa-cap-name">{cap.name}</div>
                          <div className="pa-cap-price">{Number(cap.price).toLocaleString('fa-IR')} تومان</div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Store & Category */}
                <section className="pa-section">
                  <h3 className="pa-section-title">فروشگاه و دسته‌بندی</h3>
                  <div className="pa-kv">
                    <div className="pa-kv-item"><span className="pa-kv-label">فروشگاه</span><span className="pa-kv-value">{d.storeId?.name || '-'}</span></div>
                    <div className="pa-kv-item"><span className="pa-kv-label">نوع محصول</span><span className="pa-kv-value">{d.accountTypeId?.name || '-'}</span></div>
                    {d.sku && <div className="pa-kv-item"><span className="pa-kv-label">SKU</span><span className="pa-kv-value">{d.sku}</span></div>}
                    {d.slug && <div className="pa-kv-item"><span className="pa-kv-label">Slug</span><span className="pa-kv-value">{d.slug}</span></div>}
                  </div>
                </section>

                {/* Postal info */}
                {d.isPostal && (
                  <section className="pa-section">
                    <h3 className="pa-section-title"><LocalShippingIcon fontSize="small" /> اطلاعات ارسال پستی</h3>
                    <div className="pa-kv">
                      <div className="pa-kv-item">
                        <span className="pa-kv-label">هزینه ارسال</span>
                        <span className="pa-kv-value">{d.shippingCost ? Number(d.shippingCost).toLocaleString('fa-IR') + ' تومان' : 'رایگان'}</span>
                      </div>
                      {d.deliveryTime && (
                        <div className="pa-kv-item"><span className="pa-kv-label">زمان ارسال</span><span className="pa-kv-value">{d.deliveryTime}</span></div>
                      )}
                    </div>
                  </section>
                )}

                {/* Dynamic fields */}
                {dyn.length > 0 && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">مشخصات محصول</h3>
                    <div className="pa-kv">
                      {dyn.map((field, idx) => (
                        <div key={idx} className="pa-kv-item"><span className="pa-kv-label">{field.label}</span><span className="pa-kv-value">{field.value}</span></div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Description */}
                {d.description && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">توضیحات</h3>
                    {/* توضیحات HTML غنی است؛ با DOMPurify پاکسازی و امن رندر می‌شود. */}
                    <div className="rich-text-view" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.description) }} />
                  </section>
                )}

                {/* Tags */}
                {(d.tags || []).length > 0 && (
                  <section className="pa-section">
                    <h3 className="pa-section-title">برچسب‌ها</h3>
                    <div className="pa-tags">
                      {d.tags.map((t: string, idx: number) => (
                        <span key={idx} className="tag tag-sm tag-inactive">{t}</span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Verification history */}
                {vi && (vi.approvedAt || vi.rejectedAt || vi.editRequest) && (
                  <section className="pa-section">
                    <h3 className="pa-section-title"><HistoryIcon fontSize="small" /> تاریخچه تأیید</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {vi.approvedAt && (
                        <div className="pa-verdict approved">
                          <div className="pa-verdict-head"><CheckCircleIcon fontSize="small" /> تأیید شده</div>
                          <div className="pa-verdict-grid">
                            <div><span className="pa-vk">تأیید توسط</span><span className="pa-vv">{vi.approvedBy?.name || '-'}</span></div>
                            <div><span className="pa-vk">تاریخ تأیید</span><span className="pa-vv">{formatDate(vi.approvedAt)}</span></div>
                          </div>
                          {vi.approvalNote && (
                            <div className="pa-verdict-note"><span className="pa-vk">یادداشت</span><p>{vi.approvalNote}</p></div>
                          )}
                        </div>
                      )}
                      {vi.rejectedAt && (
                        <div className="pa-verdict rejected">
                          <div className="pa-verdict-head"><BlockIcon fontSize="small" /> رد شده</div>
                          <div className="pa-verdict-grid">
                            <div><span className="pa-vk">رد توسط</span><span className="pa-vv">{vi.rejectedBy?.name || '-'}</span></div>
                            <div><span className="pa-vk">تاریخ رد</span><span className="pa-vv">{formatDate(vi.rejectedAt)}</span></div>
                          </div>
                          {vi.rejectionReason && (
                            <div className="pa-verdict-note"><span className="pa-vk">دلیل رد</span><p>{vi.rejectionReason}</p></div>
                          )}
                        </div>
                      )}
                      {vi.editRequest && (
                        <div className="pa-verdict info">
                          <div className="pa-verdict-head"><WarningAmberIcon fontSize="small" /> درخواست ویرایش</div>
                          <div className="pa-verdict-grid">
                            <div><span className="pa-vk">وضعیت</span><span className="pa-vv">{vi.editRequest.status || '-'}</span></div>
                            <div><span className="pa-vk">تاریخ درخواست</span><span className="pa-vv">{formatDate(vi.editRequest.requestDate)}</span></div>
                          </div>
                          {vi.editRequest.rejectionReason && (
                            <div className="pa-verdict-note"><span className="pa-vk">دلیل رد ویرایش</span><p>{vi.editRequest.rejectionReason}</p></div>
                          )}
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Standalone rejection reason */}
                {d.rejectionReason && !vi?.rejectionReason && (
                  <section className="pa-section">
                    <h3 className="pa-section-title"><BlockIcon fontSize="small" /> دلیل رد</h3>
                    <div className="pa-verdict rejected">
                      <div className="pa-verdict-note" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                        <p>{d.rejectionReason}</p>
                      </div>
                    </div>
                  </section>
                )}
              </>
            );
          })() : null}
        </DialogContent>

        <div className="pa-details-actions">
          <button className="pa-btn ghost" onClick={closeDetail}>بستن</button>
        </div>

        {/* Image lightbox */}
        {lightboxSrc && (
          <div className="pa-lightbox" onClick={() => setLightboxSrc(null)}>
            <button className="pa-lightbox-close" onClick={() => setLightboxSrc(null)} aria-label="بستن"><CloseIcon /></button>
            <img src={lightboxSrc} alt="پیش‌نمایش" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </Dialog>

      {/* Publish Confirm Dialog */}
      <ConfirmDialog
        open={publishOpen}
        title="انتشار محصول"
        message={
          <span>
            آیا از انتشار محصول <strong>«{currentRow?.name}»</strong> اطمینان دارید؟
            <br />با انتشار، محصول در فروشگاه قابل مشاهده خواهد بود.
          </span>
        }
        cancelText="انصراف"
        confirmText="انتشار"
        confirmColor="success"
        loading={actionLoading}
        onCancel={closeActions}
        onConfirm={handlePublish}
      />

      {/* Unpublish Confirm Dialog */}
      <ConfirmDialog
        open={unpublishOpen}
        title="توقف محصول"
        message={
          <span>
            آیا از توقف محصول <strong>«{currentRow?.name}»</strong> اطمینان دارید؟
            <br />با توقف، محصول از نمایش در فروشگاه خارج می‌شود و بعداً قابل انتشار مجدد است.
          </span>
        }
        cancelText="انصراف"
        confirmText="توقف"
        confirmColor="error"
        loading={actionLoading}
        onCancel={closeActions}
        onConfirm={handleUnpublish}
      />
    </div>
  );
};

export default ProductManagement;
